import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Box, DataTable, Button, Modal, TextField, FormLayout, ButtonGroup, InlineStack, Text, Select, RadioButton, InlineError, Checkbox } from '@shopify/polaris';
import { EditIcon, DeleteIcon } from '@shopify/polaris-icons';
import { useToast } from '#client/components/common/Toast/ToastContext';

function ReasonCodeManage() {
    const { addToast, clearToasts } = useToast();
    const queryClient = useQueryClient();
    const [reasonCodes, setReasonCodes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [deletingCode, setDeletingCode] = useState(null);
    const [formData, setFormData] = useState({
        reasonCode: '',
        shopifyReasonCode: '',
        step: 1,
        isActive: true,
    });
    const [formErrors, setFormErrors] = useState({});

    // Valid Shopify reason codes
    const shopifyReasonCodes = [
        { value: 'correction', label: 'correction - Correct an inventory error or general adjustment' },
        { value: 'cycle_count_available', label: 'cycle_count_available - Adjusted count due to discrepancy' },
        { value: 'damaged', label: 'damaged - Remove units due to damage' },
        { value: 'movement_created', label: 'movement_created - Inventory transfer or PO created' },
        { value: 'movement_updated', label: 'movement_updated - Inventory transfer or PO updated' },
        { value: 'movement_received', label: 'movement_received - Inventory transfer or PO received' },
        { value: 'movement_canceled', label: 'movement_canceled - Inventory transfer or PO canceled' },
        { value: 'other', label: 'other - Alternate reason for adjustment' },
        { value: 'promotion', label: 'promotion - Remove units due to promotion or donation' },
        { value: 'quality_control', label: 'quality_control - Units in inspection for quality' },
        { value: 'received', label: 'received - Inventory received by merchant' },
        { value: 'reservation_created', label: 'reservation_created - Reserve unavailable units' },
        { value: 'reservation_deleted', label: 'reservation_deleted - Remove reserved units' },
        { value: 'reservation_updated', label: 'reservation_updated - Update reserved units' },
        { value: 'restock', label: 'restock - Add returned unit back to available inventory' },
        { value: 'safety_stock', label: 'safety_stock - Units set aside to guard against overselling' },
        { value: 'shrinkage', label: 'shrinkage - Inventory less than recorded due to theft or loss' },
    ];

    const fetchReasonCodes = async () => {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/admin/reason-codes`);
        return response.data.data.reasonCodes;
    };

    const { data: reasonCodesData, isLoading } = useQuery({
        queryKey: ['adminReasonCodes'],
        queryFn: fetchReasonCodes,
        staleTime: 'static',
    });

    useEffect(() => {
        if (reasonCodesData) setReasonCodes(reasonCodesData);
    }, [reasonCodesData]);

    // Create mutation
    const createMutation = useMutation({
        mutationFn: async (newReasonCode) => {
            const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/app/admin/reason-codes`, newReasonCode);
            return response.data.data.reasonCode;
        },
        onMutate: async (newReasonCode) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['adminReasonCodes'] });
            await queryClient.cancelQueries({ queryKey: ['inventory-adjustment-reason-codes'] });

            // Snapshot previous values
            const previousAdminReasonCodes = queryClient.getQueryData(['adminReasonCodes']);
            const previousAllReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes']);
            const previousActiveReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes', 'active']);

            // Optimistically update cache with temporary ID
            const optimisticReasonCode = {
                id: Date.now(), // Temporary ID
                ...newReasonCode,
                createdAt: new Date().toISOString(),
                usageCount: 0,
            };

            // Update admin cache
            queryClient.setQueryData(['adminReasonCodes'], (old) => [...(old || []), optimisticReasonCode]);

            // Update all reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], (old) => [...(old || []), optimisticReasonCode]);

            // Update active reason codes cache if the new code is active
            if (newReasonCode.isActive) {
                queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], (old) => [...(old || []), optimisticReasonCode]);
            }

            return { previousAdminReasonCodes, previousAllReasonCodes, previousActiveReasonCodes };
        },
        onError: (err, newReasonCode, context) => {
            // Rollback on error
            queryClient.setQueryData(['adminReasonCodes'], context.previousAdminReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], context.previousAllReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], context.previousActiveReasonCodes);
            clearToasts();
            addToast({
                type: 'error',
                message: 'Failed to create reason code. Please try again.',
            });
        },
        onSuccess: (data) => {
            // Update admin cache with real server data
            queryClient.setQueryData(['adminReasonCodes'], (old) => {
                const filtered = (old || []).filter((code) => code.id !== data.id && code.id < 1000000000000);
                return [...filtered, data];
            });

            // Update all reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], (old) => {
                const filtered = (old || []).filter((code) => code.id !== data.id && code.id < 1000000000000);
                return [...filtered, data];
            });

            // Update active reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], (old) => {
                const filtered = (old || []).filter((code) => code.id !== data.id && code.id < 1000000000000);
                return data.isActive ? [...filtered, data] : filtered;
            });
        },
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, updates }) => {
            const response = await axios.patch(`${import.meta.env.VITE_API_URL}/api/app/admin/reason-codes/${id}`, updates);
            return response.data.data.reasonCode;
        },
        onMutate: async ({ id, updates }) => {
            await queryClient.cancelQueries({ queryKey: ['adminReasonCodes'] });
            await queryClient.cancelQueries({ queryKey: ['inventory-adjustment-reason-codes'] });

            const previousAdminReasonCodes = queryClient.getQueryData(['adminReasonCodes']);
            const previousAllReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes']);
            const previousActiveReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes', 'active']);

            // Optimistically update admin cache
            queryClient.setQueryData(['adminReasonCodes'], (old) => (old || []).map((code) => (code.id === id ? { ...code, ...updates } : code)));

            // Optimistically update all reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], (old) => (old || []).map((code) => (code.id === id ? { ...code, ...updates } : code)));

            // Optimistically update active reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], (old) => {
                const filtered = (old || []).filter((code) => code.id !== id);
                const existingCode = (old || []).find((code) => code.id === id);
                const updatedCode = existingCode ? { ...existingCode, ...updates } : null;

                // Add back if active, otherwise exclude
                return updatedCode && updates.isActive !== false ? [...filtered, updatedCode] : filtered;
            });

            return { previousAdminReasonCodes, previousAllReasonCodes, previousActiveReasonCodes };
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(['adminReasonCodes'], context.previousAdminReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], context.previousAllReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], context.previousActiveReasonCodes);
            clearToasts();
            addToast({
                type: 'error',
                message: 'Failed to update reason code. Please try again.',
            });
        },
        onSuccess: (data) => {
            // Update admin cache with real server data
            queryClient.setQueryData(['adminReasonCodes'], (old) => (old || []).map((code) => (code.id === data.id ? data : code)));

            // Update all reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], (old) => (old || []).map((code) => (code.id === data.id ? data : code)));

            // Update active reason codes cache
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], (old) => {
                const filtered = (old || []).filter((code) => code.id !== data.id);
                return data.isActive ? [...filtered, data] : filtered;
            });
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            await axios.delete(`${import.meta.env.VITE_API_URL}/api/app/admin/reason-codes/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: ['adminReasonCodes'] });
            await queryClient.cancelQueries({ queryKey: ['inventory-adjustment-reason-codes'] });

            const previousAdminReasonCodes = queryClient.getQueryData(['adminReasonCodes']);
            const previousAllReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes']);
            const previousActiveReasonCodes = queryClient.getQueryData(['inventory-adjustment-reason-codes', 'active']);

            // Optimistically remove from all caches
            queryClient.setQueryData(['adminReasonCodes'], (old) => (old || []).filter((code) => code.id !== id));
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], (old) => (old || []).filter((code) => code.id !== id));
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], (old) => (old || []).filter((code) => code.id !== id));

            return { previousAdminReasonCodes, previousAllReasonCodes, previousActiveReasonCodes };
        },
        onError: (err, id, context) => {
            queryClient.setQueryData(['adminReasonCodes'], context.previousAdminReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes'], context.previousAllReasonCodes);
            queryClient.setQueryData(['inventory-adjustment-reason-codes', 'active'], context.previousActiveReasonCodes);
            clearToasts();
            addToast({
                type: 'error',
                message: 'Failed to delete reason code. Please try again.',
            });
        },
    });

    const handleOpenModal = (code = null) => {
        if (code) {
            setEditingCode(code);
            setFormData({
                reasonCode: code.reasonCode,
                shopifyReasonCode: code.shopifyReasonCode,
                step: code.step.toString(),
                isActive: code.isActive,
            });
        } else {
            setEditingCode(null);
            setFormData({
                reasonCode: '',
                shopifyReasonCode: '',
                step: '1',
                isActive: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCode(null);
        setFormData({
            reasonCode: '',
            shopifyReasonCode: '',
            step: '',
            isActive: true,
        });
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.reasonCode.trim()) {
            errors.reasonCode = 'Reason code is required';
        }
        if (!formData.shopifyReasonCode.trim()) {
            errors.shopifyReasonCode = 'Shopify reason code is required';
        } else {
            const validCodes = shopifyReasonCodes.map((code) => code.value);
            if (!validCodes.includes(formData.shopifyReasonCode)) {
                errors.shopifyReasonCode = 'Invalid Shopify reason code';
            }
        }
        if (!formData.step || isNaN(Number(formData.step))) {
            errors.step = 'Default adjustment direction is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        const payload = {
            reasonCode: formData.reasonCode,
            shopifyReasonCode: formData.shopifyReasonCode,
            step: Number(formData.step),
            isActive: formData.isActive,
        };

        try {
            if (editingCode) {
                await updateMutation.mutateAsync({
                    id: editingCode.id,
                    updates: payload,
                });
            } else {
                await createMutation.mutateAsync(payload);
            }
            handleCloseModal();
        } catch (error) {
            console.error('Failed to save reason code:', error);
            // Error handling is done in mutation's onError
        }
    };

    const handleOpenDeleteModal = (code) => {
        setDeletingCode(code);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingCode(null);
    };

    const handleDelete = async () => {
        if (!deletingCode) return;

        try {
            await deleteMutation.mutateAsync(deletingCode.id);
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Failed to delete reason code:', error);
            // Error handling is done in mutation's onError
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const rows = reasonCodes.map((code) => [
        code.id,
        code.reasonCode,
        code.shopifyReasonCode,
        code.step === 1 ? 'Increment' : 'Decrement',
        formatDate(code.createdAt),
        code.usageCount,
        code.isActive ? 'Active' : 'Inactive',
        <ButtonGroup gap="tight" key={code.id}>
            <Button icon={EditIcon} onClick={() => handleOpenModal(code)} size="slim" variant="plain" accessibilityLabel={`Edit ${code.reasonCode}`} />
            <Button
                disabled={code.usageCount > 0}
                title="Cannot delete reason code with existing adjustments"
                icon={DeleteIcon}
                onClick={() => handleOpenDeleteModal(code)}
                size="slim"
                variant="plain"
                tone="critical"
                accessibilityLabel={`Delete ${code.reasonCode}`}
            />
        </ButtonGroup>,
    ]);

    if (isLoading) {
        return <Card>Loading...</Card>;
    }

    return (
        <>
            <Card title="Inventory Reason Codes">
                <Box paddingBlockEnd="400">
                    <InlineStack align="end">
                        <Button variant="primary" onClick={() => handleOpenModal()}>
                            Create Reason Code
                        </Button>
                    </InlineStack>
                </Box>
                <DataTable
                    columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'numberic', 'text', 'text']}
                    headings={['ID', 'Reason Code', 'Shopify Reason Code', 'Default Direction', 'Created At', 'Usage Count', 'Status', 'Actions']}
                    rows={rows}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                open={showModal}
                onClose={handleCloseModal}
                title={editingCode ? 'Edit Reason Code' : 'Create Reason Code'}
                primaryAction={{
                    content: editingCode ? 'Save' : 'Create',
                    onAction: handleSave,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: handleCloseModal,
                    },
                ]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Reason Code"
                            value={formData.reasonCode}
                            onChange={(value) => setFormData({ ...formData, reasonCode: value })}
                            error={formErrors.reasonCode}
                            autoComplete="off"
                            helpText="Display name for the reason code (e.g., 'Damaged Goods')"
                        />
                        <Select
                            label="Shopify Reason Code"
                            options={shopifyReasonCodes}
                            value={formData.shopifyReasonCode}
                            onChange={(value) => setFormData({ ...formData, shopifyReasonCode: value })}
                            error={formErrors.shopifyReasonCode}
                            placeholder="Select a Shopify reason code"
                            helpText="Official Shopify API reason code for inventory adjustments"
                        />
                        <Text>Default Adjustment Direction</Text>
                        <InlineStack gap="100">
                            <RadioButton name="step" label="Increment" value="1" checked={formData.step.toString() === '1'} onChange={() => setFormData({ ...formData, step: '1' })} />
                            <RadioButton name="step" label="Decrement" value="-1" checked={formData.step.toString() === '-1'} onChange={() => setFormData({ ...formData, step: '-1' })} />
                        </InlineStack>
                        <Text>Reason Code Status</Text>
                        {formErrors.step && <InlineError message="Default adjustment direction is required" fieldID="step" />}
                        <Checkbox name="isActive" label="Active" checked={formData.isActive} onChange={() => setFormData({ ...formData, isActive: !formData.isActive })} />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={showDeleteModal}
                onClose={handleCloseDeleteModal}
                title="Delete Reason Code"
                primaryAction={{
                    content: 'Delete',
                    onAction: handleDelete,
                    destructive: true,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: handleCloseDeleteModal,
                    },
                ]}
            >
                <Modal.Section>
                    <Text as="p">Are you sure you want to delete the reason code &quot;{deletingCode?.reasonCode}&quot;? This action cannot be undone.</Text>
                </Modal.Section>
            </Modal>
        </>
    );
}

export default ReasonCodeManage;
